import { alpha, Box, Button, IconButton, Paper, styled, Theme } from '@mui/material';

export interface GlassmorphismOptions {
  intensity?: 'light' | 'medium' | 'strong';
  tint?: 'neutral' | 'primary' | 'success' | 'warning' | 'error';
  elevation?: 'low' | 'medium' | 'high';
  interactive?: boolean;
}

// 기본 glassmorphism 스타일 생성 함수
export const createGlassmorphismStyles = (theme: Theme, options: GlassmorphismOptions = {}) => {
  const {
    intensity = 'medium',
    tint = 'neutral',
    elevation = 'medium',
    interactive = true,
  } = options;

  const isLight = theme.palette.mode === 'light';

  // 강도에 따른 투명도 설정 (라이트 모드에서 더 투명하게)
  const intensityMap = isLight
    ? {
        light: { bg: 0.2, border: 0.4, blur: 20, saturate: 200 },
        medium: { bg: 0.3, border: 0.5, blur: 24, saturate: 200 },
        strong: { bg: 0.4, border: 0.6, blur: 28, saturate: 200 },
      }
    : {
        light: { bg: 0.15, border: 0.1, blur: 15, saturate: 180 },
        medium: { bg: 0.25, border: 0.2, blur: 20, saturate: 180 },
        strong: { bg: 0.35, border: 0.3, blur: 25, saturate: 180 },
      };

  // 색조에 따른 배경색 설정
  const tintMap = {
    neutral: theme.palette.background.paper,
    primary: theme.palette.primary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
  };

  // 고도에 따른 그림자 설정
  const elevationMap = isLight
    ? {
        low: `0 4px 16px 0 ${alpha(theme.palette.common.black, 0.08)}`,
        medium: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.12)}`,
        high: `0 12px 48px 0 ${alpha(theme.palette.common.black, 0.16)}`,
      }
    : {
        low: `0 4px 16px 0 ${alpha(theme.palette.common.black, 0.1)}`,
        medium: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.15)}`,
        high: `0 12px 48px 0 ${alpha(theme.palette.common.black, 0.2)}`,
      };

  const config = intensityMap[intensity];
  const baseColor = tintMap[tint];

  return {
    backgroundColor: alpha(baseColor, config.bg),
    backdropFilter: `blur(${config.blur}px) saturate(${config.saturate}%)`,
    WebkitBackdropFilter: `blur(${config.blur}px) saturate(${config.saturate}%)`,
    border: `1px solid ${alpha(theme.palette.common.white, config.border)}`,
    boxShadow: `
      ${elevationMap[elevation]},
      inset 0 1px 0 0 ${alpha(theme.palette.common.white, isLight ? 0.8 : 0.4)},
      inset 0 0 20px 0 ${alpha(theme.palette.common.white, isLight ? 0.1 : 0.05)}
    `,
    position: 'relative' as const,
    overflow: 'hidden' as const,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

    ...(interactive && {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: alpha(baseColor, config.bg + (isLight ? 0.05 : 0.1)),
        border: `1px solid ${alpha(theme.palette.common.white, config.border + 0.1)}`,
        boxShadow: `
          ${elevationMap[elevation]},
          inset 0 1px 0 0 ${alpha(theme.palette.common.white, isLight ? 0.9 : 0.5)},
          inset 0 0 20px 0 ${alpha(theme.palette.common.white, isLight ? 0.15 : 0.08)}
        `,
        transform: 'translateY(-2px)',
      },
      '&:active': {
        transform: 'translateY(0px)',
      },
    }),
  };
};

// 반사 효과 스타일
export const createReflectionEffect = (theme: Theme) => {
  const isLight = theme.palette.mode === 'light';
  return {
    '&::before': {
      content: '""',
      position: 'absolute' as const,
      top: 0,
      left: '-50%',
      width: '50%',
      height: '100%',
      background: `linear-gradient(90deg, transparent, ${alpha(
        theme.palette.common.white,
        isLight ? 0.5 : 0.2,
      )}, transparent)`,
      transform: 'skewX(-25deg)',
      transition: 'left 0.6s ease',
      pointerEvents: 'none' as const,
    },
    '&:hover::before': {
      left: '100%',
    },
  };
};

// 펄스 효과 (로딩이나 활성 상태용)
export const createPulseEffect = (theme: Theme, color?: string) => ({
  '&::after': {
    content: '""',
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    width: '0',
    height: '0',
    borderRadius: '50%',
    background: alpha(color || theme.palette.primary.main, 0.3),
    transform: 'translate(-50%, -50%)',
    animation: 'glassPulse 2s infinite',
  },
  '@keyframes glassPulse': {
    '0%': {
      width: '0',
      height: '0',
      opacity: 1,
    },
    '100%': {
      width: '100%',
      height: '100%',
      opacity: 0,
    },
  },
});

// 상승 애니메이션
export const createFloatingEffect = () => ({
  animation: 'glassFloat 3s ease-in-out infinite',
  '@keyframes glassFloat': {
    '0%, 100%': {
      transform: 'translateY(0px)',
    },
    '50%': {
      transform: 'translateY(-5px)',
    },
  },
});

// 미리 정의된 glassmorphism 프리셋
export const glassPresets = {
  card: { intensity: 'medium' as const, tint: 'neutral' as const, elevation: 'medium' as const },
  button: { intensity: 'light' as const, tint: 'neutral' as const, elevation: 'low' as const },
  modal: { intensity: 'strong' as const, tint: 'neutral' as const, elevation: 'high' as const },
  header: { intensity: 'light' as const, tint: 'neutral' as const, elevation: 'low' as const },
  sidebar: { intensity: 'medium' as const, tint: 'neutral' as const, elevation: 'medium' as const },
  success: { intensity: 'medium' as const, tint: 'success' as const, elevation: 'medium' as const },
  warning: { intensity: 'medium' as const, tint: 'warning' as const, elevation: 'medium' as const },
  error: { intensity: 'medium' as const, tint: 'error' as const, elevation: 'medium' as const },
};

// Glass Box 컴포넌트
export const GlassBox = styled(Box, {
  shouldForwardProp: (prop) =>
    !['glassOptions', 'withReflection', 'withPulse', 'withFloat'].includes(prop as string),
})<{
  glassOptions?: GlassmorphismOptions;
  withReflection?: boolean;
  withPulse?: boolean;
  withFloat?: boolean;
}>(({ theme, glassOptions, withReflection, withPulse, withFloat }) => ({
  ...createGlassmorphismStyles(theme, glassOptions),
  ...(withReflection && createReflectionEffect(theme)),
  ...(withPulse && createPulseEffect(theme)),
  ...(withFloat && createFloatingEffect()),
}));

// Glass Paper 컴포넌트
export const GlassPaper = styled(Paper, {
  shouldForwardProp: (prop) => !['glassOptions', 'withReflection'].includes(prop as string),
})<{
  glassOptions?: GlassmorphismOptions;
  withReflection?: boolean;
}>(({ theme, glassOptions, withReflection }) => ({
  ...createGlassmorphismStyles(theme, glassOptions),
  ...(withReflection && createReflectionEffect(theme)),
}));

// Glass Button 컴포넌트
export const GlassButton = styled(Button, {
  shouldForwardProp: (prop) => !['glassOptions', 'withReflection'].includes(prop as string),
})<{ glassOptions?: GlassmorphismOptions; withReflection?: boolean }>(
  ({ theme, glassOptions, withReflection }) => ({
    ...createGlassmorphismStyles(theme, glassOptions),
    ...(withReflection && createReflectionEffect(theme)),
    borderRadius: theme.shape.borderRadius * 3,
  }),
);

// Glass Icon Button 컴포넌트
export const GlassIconButton = styled(IconButton, {
  shouldForwardProp: (prop) =>
    !['glassOptions', 'withReflection', 'withPulse'].includes(prop as string),
})<{
  glassOptions?: GlassmorphismOptions;
  withReflection?: boolean;
  withPulse?: boolean;
}>(({ theme, glassOptions, withReflection, withPulse, color }) => {
  // color prop이 있으면 해당 색상을 배경색으로 사용
  const getBackgroundColor = () => {
    if (!color || color === 'default' || color === 'inherit') {
      return undefined;
    }

    const colorMap = {
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main,
      error: theme.palette.error.main,
      warning: theme.palette.warning.main,
      info: theme.palette.info.main,
      success: theme.palette.success.main,
    };

    return colorMap[color as keyof typeof colorMap];
  };

  const backgroundColor = getBackgroundColor();

  const finalGlassOptions = backgroundColor
    ? { ...glassOptions, tint: 'neutral' as const }
    : glassOptions || {
        intensity: 'light' as const,
        tint: 'neutral' as const,
        elevation: 'low' as const,
      };

  // 기존 glassmorphism 스타일 가져오기
  const glassStyles = createGlassmorphismStyles(theme, finalGlassOptions);

  return {
    ...glassStyles,
    ...(withReflection && createReflectionEffect(theme)),
    borderRadius: '50%',

    ...(backgroundColor && {
      backgroundColor: alpha(backgroundColor, 0.8),
      color: theme.palette.getContrastText(backgroundColor),
      '&:hover': {
        ...glassStyles['&:hover'],

        backgroundColor: alpha(backgroundColor, 0.9),
        color: theme.palette.getContrastText(backgroundColor),
      },
    }),
  };
});

// Hook 형태로 스타일 사용
export const useGlassmorphism = (options?: GlassmorphismOptions) => {
  return (theme: Theme) => createGlassmorphismStyles(theme, options);
};

const glassmorphism = {
  createGlassmorphismStyles,
  createReflectionEffect,
  createPulseEffect,
  createFloatingEffect,
  glassPresets,
  GlassBox,
  GlassPaper,
  GlassButton,
  GlassIconButton,
  useGlassmorphism,
};

export default glassmorphism;
